if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.stickyButton = this.querySelector('.sticky-add-to-cart__button');
        this.variantSelect = this.querySelector('.sticky-add-to-cart__select');
        this.variants = this.getVariants();
        this.originalButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        this.variantInput = document.querySelector(`#${this.dataset.formId} input[name="id"]`);
        if (!this.stickyButton || !this.originalButton) return;

        this.stickyButton.addEventListener('click', this.onClick);
        this.variantSelect?.addEventListener('change', this.onVariantSelect);
        this.variantInput?.addEventListener('change', this.onOriginalVariantChange);
        document.addEventListener('change', this.onDocumentVariantChange);
        this.observer = new IntersectionObserver(this.onIntersection, { threshold: 0 });
        this.observer.observe(this.originalButton);
        this.buttonObserver = new MutationObserver(this.syncButton);
        this.buttonObserver.observe(this.originalButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true
        });
        this.syncButton();
        if (this.variantInput) this.syncVariantById(this.variantInput.value);
        this.variantSyncTimer = window.setInterval(this.syncFromProductForm, 250);

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId) return;
            if (this.variantInput && String(event.data.variant?.id) !== String(this.variantInput.value)) return;
            this.syncSelectedVariant(event.data.variant);
            requestAnimationFrame(this.syncButton);
          });
        }
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener('click', this.onClick);
        this.variantSelect?.removeEventListener('change', this.onVariantSelect);
        this.variantInput?.removeEventListener('change', this.onOriginalVariantChange);
        document.removeEventListener('change', this.onDocumentVariantChange);
        this.observer?.disconnect();
        this.buttonObserver?.disconnect();
        window.clearInterval(this.variantSyncTimer);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onClick = () => {
        if (this.stickyButton.disabled) return;
        this.originalButton.click();
      };

      onVariantSelect = () => {
        const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
        if (!variant) return;

        const picker =
          document.getElementById(`variant-selects-${this.dataset.sectionId}`) ||
          document.getElementById(`variant-radios-${this.dataset.sectionId}`);
        if (!picker) return;

        let changedControl;

        if (picker.tagName === 'VARIANT-SELECTS') {
          const selects = Array.from(picker.querySelectorAll('select'));
          selects.forEach((select, index) => {
            select.value = variant.options[index];
            changedControl = select;
          });
        } else {
          const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
          fieldsets.forEach((fieldset, index) => {
            const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
              (radio) => radio.value === variant.options[index]
            );
            if (input) {
              input.checked = true;
              changedControl = input;
            }
          });
        }

        changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
      };

      onOriginalVariantChange = () => {
        this.syncVariantById(this.variantInput.value);
      };

      onDocumentVariantChange = (event) => {
        const picker = event.target.closest?.(
          `variant-selects[data-section="${this.dataset.sectionId}"], variant-radios[data-section="${this.dataset.sectionId}"]`
        );
        if (!picker) return;

        requestAnimationFrame(() => {
          const variantId = picker.currentVariant?.id || this.variantInput?.value;
          if (variantId) this.syncVariantById(variantId);
        });
      };

      onIntersection = ([entry]) => {
        const hasPassedButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', hasPassedButton);
        this.setAttribute('aria-hidden', String(!hasPassedButton));
      };

      syncButton = () => {
        const isDisabled =
          this.originalButton.disabled || this.originalButton.getAttribute('aria-disabled') === 'true';
        const isLoading = this.originalButton.classList.contains('loading');
        const originalText = this.originalButton.querySelector('span')?.textContent?.trim();

        this.stickyButton.disabled = isDisabled;
        this.stickyButton.classList.toggle('loading', isLoading);
        this.stickyButton.querySelector('.sticky-add-to-cart__spinner')?.classList.toggle('hidden', !isLoading);
        const buttonText = isDisabled ? originalText : this.dataset.buttonLabel;
        if (buttonText) this.stickyButton.querySelector(':scope > span').textContent = buttonText;
      };

      syncSelectedVariant(variant) {
        if (!variant || !this.variantSelect) return;
        this.variantSelect.value = String(variant.id);

        const variantLabel = this.querySelector('.sticky-add-to-cart__variant');
        if (variantLabel) variantLabel.textContent = variant.title;

        const price = this.querySelector('.sticky-add-to-cart__price');
        if (price) price.textContent = this.formatMoney(variant.price);

        if (variant.featured_media?.preview_image?.src) {
          const image = this.querySelector('.sticky-add-to-cart__image');
          if (image) image.src = variant.featured_media.preview_image.src;
        }
      }

      syncVariantById(variantId) {
        const variant = this.variants.find((item) => String(item.id) === String(variantId));
        if (variant) this.syncSelectedVariant(variant);
      }

      syncFromProductForm = () => {
        const picker =
          document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`) ||
          document.querySelector(`variant-radios[data-section="${this.dataset.sectionId}"]`);
        const currentInput = document.querySelector(`#${this.dataset.formId} input[name="id"]`);
        const currentVariantId = picker?.currentVariant?.id || currentInput?.value;
        if (!currentVariantId || String(currentVariantId) === this.variantSelect?.value) return;

        if (currentInput && currentInput !== this.variantInput) {
          this.variantInput?.removeEventListener('change', this.onOriginalVariantChange);
          this.variantInput = currentInput;
          this.variantInput.addEventListener('change', this.onOriginalVariantChange);
        }

        this.syncVariantById(currentVariantId);
      };

      getVariants() {
        const data = this.querySelector('[data-sticky-variants]');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent);
        } catch (error) {
          console.error('Unable to load sticky cart variants.', error);
          return [];
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const type = placeholder ? placeholder[1] : 'amount';
        const amount = cents / 100;
        let value;

        if (type === 'amount_no_decimals') {
          value = Math.round(amount).toLocaleString();
        } else {
          value = amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }

        return placeholder ? format.replace(placeholder[0], value) : value;
      }
    }
  );
}
